import { api } from './api';
import type { Experiment as ExperimentType, ExperimentResult, ValidationGateResult, CreateExperimentRequest } from '../types/experiment.types';

// Experiment interfaces
export interface Experiment {
  id: number;
  name: string;
  description: string;
  siteId: number;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'CANCELLED';
  startDate: string;
  endDate: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

export interface ExperimentGroup {
  id: number;
  experimentId: number;
  groupType: 'TEST' | 'CONTROL';
  name: string;
  description: string;
  urlCount: number;
}

export interface MetricAnalysis {
  metric: string;
  testMean: number;
  controlMean: number;
  difference: number;
  percentageChange: number;
  pValue: number;
  significant: boolean;
  winner: 'TEST' | 'CONTROL' | 'INCONCLUSIVE';
  confidence: number;
  effectSize: number;
  sampleSize: number;
}

export interface ExperimentAnalysis {
  experimentId: number;
  experimentName: string;
  overallWinner: 'TEST' | 'CONTROL' | 'INCONCLUSIVE';
  analysisDate: string;
  metrics: MetricAnalysis[];
  summary: {
    totalDays: number;
    testUrlCount: number;
    controlUrlCount: number;
    confidence: number;
  };
}

export interface ExperimentSummary {
  id: number;
  name: string;
  status: string;
  startDate: string;
  endDate: string;
  testGroupSize: number;
  controlGroupSize: number;
  daysRunning: number;
}

export interface PowerAnalysisRequest {
  expectedEffectSize: number;
  alpha: number;
  power: number;
}

export interface PowerAnalysisResult {
  requiredSampleSizePerGroup: number;
  expectedPower: number;
  effectSize: number;
  alpha: number;
  beta: number;
  recommendation: string;
}

// Backend API response wrapper
interface ApiResponse<T> {
  data: T;
  success: boolean;
  requestId: string;
  timestamp: number;
}

export const experimentService = {
  // Get all experiments
  async getExperiments(status?: string): Promise<Experiment[]> {
    const response = await api.get<Experiment[]>('/coreapi/experiments', {
      params: status ? { status } : {},
    });
    return response.data;
  },

  // Get experiment by ID
  async getExperiment(experimentId: number): Promise<Experiment> {
    const response = await api.get<Experiment>(`/coreapi/experiments/${experimentId}`);
    return response.data;
  },

  // Get experiment groups
  async getExperimentGroups(experimentId: number): Promise<ExperimentGroup[]> {
    const response = await api.get<ApiResponse<ExperimentGroup[]>>(`/coreapi/experiments/${experimentId}/groups`);
    return response.data.data;
  },

  // Get experiment analysis
  async getExperimentAnalysis(experimentId: number): Promise<ExperimentAnalysis> {
    const response = await api.get<ApiResponse<ExperimentAnalysis>>(`/coreapi/experiments/${experimentId}/analysis`);
    return response.data.data;
  },

  // Get experiment summary
  async getExperimentSummary(experimentId: number): Promise<ExperimentSummary> {
    const response = await api.get<ApiResponse<ExperimentSummary>>(`/coreapi/experiments/${experimentId}/summary`);
    return response.data.data;
  },

  // Get active experiments
  async getActiveExperiments(): Promise<Experiment[]> {
    return this.getExperiments('RUNNING');
  },

  // Get completed experiments
  async getCompletedExperiments(): Promise<Experiment[]> {
    return this.getExperiments('COMPLETED');
  },

  // Get experiments ending soon
  async getExperimentsEndingSoon(days: number = 7): Promise<Experiment[]> {
    const experiments = await this.getExperiments('RUNNING');
    // Filter on client side for now
    const now = new Date();
    const futureDate = new Date();
    futureDate.setDate(now.getDate() + days);
    
    return experiments.filter((exp: Experiment) => {
      const endDate = new Date(exp.endDate);
      return endDate >= now && endDate <= futureDate;
    });
  },

  // Create experiment
  async createExperiment(data: any): Promise<Experiment> {
    const response = await api.post<Experiment>('/experiments', data);
    return response.data;
  },

  // Start experiment
  async startExperiment(experimentId: number): Promise<void> {
    await api.post(`/experiments/${experimentId}/start`);
  },

  // Stop experiment
  async stopExperiment(experimentId: number): Promise<void> {
    await api.post(`/experiments/${experimentId}/stop`);
  },

  // Cancel experiment
  async cancelExperiment(experimentId: number, reason?: string): Promise<void> {
    await api.post(`/experiments/${experimentId}/cancel`, {
      reason: reason || 'User requested cancellation'
    });
  },

  // Feature 03: Experiment Validation Engine methods (v2 API)
  async getExperimentV2(experimentId: number) {
    return api.get(`/v2/experiments/${experimentId}`);
  },

  async getExperimentResults(experimentId: number): Promise<ExperimentResult[]> {
    const response = await api.get<{ data: ExperimentResult[] }>(`/v2/experiments/${experimentId}/results`);
    return response.data.data;
  },

  async getValidationGate(experimentId: number): Promise<ValidationGateResult> {
    const response = await api.get<{ data: ValidationGateResult }>(`/v2/experiments/${experimentId}/validation-gate`);
    return response.data.data;
  },

  async createExperimentV2(data: CreateExperimentRequest): Promise<ExperimentType> {
    const response = await api.post<{ data: ExperimentType }>('/v2/experiments', data);
    return response.data.data;
  },

  async startExperimentV2(experimentId: number): Promise<ExperimentType> {
    const response = await api.post<{ data: ExperimentType }>(`/v2/experiments/${experimentId}/start`);
    return response.data.data;
  },

  async closeExperiment(experimentId: number): Promise<ExperimentType> {
    const response = await api.post<{ data: ExperimentType }>(`/v2/experiments/${experimentId}/close`);
    return response.data.data;
  },

  async deleteExperiment(experimentId: number): Promise<void> {
    await api.delete(`/v2/experiments/${experimentId}`);
  },

  async duplicateExperiment(experimentId: number): Promise<ExperimentType> {
    const response = await api.post<{ data: ExperimentType }>(`/v2/experiments/${experimentId}/duplicate`);
    return response.data.data;
  },

  // Power analysis for sample size calculation
  async calculatePowerAnalysis(request: PowerAnalysisRequest): Promise<PowerAnalysisResult> {
    const response = await api.post<PowerAnalysisResult>('/experiments/statistical/power-analysis', request);
    return response.data;
  },
};
